import { useEffect, useRef, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { LiveFeedMessage, LiveSessionStatus, NormalizedComment } from "../generated/core-types";
import { ServerConfig, fullWSURL } from "../serverConfig";
import { palette } from "../theme";
import { CapturedComment, parseCapturedComments } from "./capturedComments";

interface Props {
  serverConfig: ServerConfig;
  onStatus: (status: LiveSessionStatus) => void;
  onTransport: (state: "connecting" | "connected" | "disconnected") => void;
  onCount?: (count: number) => void;
  search?: string;
}

export function LiveComments(props: Props): JSX.Element {
  const [comments, setComments] = useState<NormalizedComment[]>([]);
  const [capturedById, setCapturedById] = useState<Map<string, CapturedComment>>(new Map());
  const [status, setStatus] = useState<LiveSessionStatus | null>(null);
  const callbacks = useRef(props);
  callbacks.current = props;
  const wsUrl = fullWSURL(props.serverConfig) + "/live/comments";
  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let retries = 0;
    let revision = -1;
    let sessionId: string | null = null;
    const connect = () => {
      if (disposed) return;
      callbacks.current.onTransport("connecting");
      const ws = new WebSocket(wsUrl);
      socket = ws;
      ws.onmessage = event => {
        if (disposed || socket !== ws) return;
        try {
          const frame = JSON.parse(String(event.data)) as LiveFeedMessage;
          if (!frame.status || !Array.isArray(frame.comments) ||
              !["snapshot", "status", "comments"].includes(frame.kind)) throw new Error("Invalid feed");
          if (frame.kind !== "snapshot" && frame.revision <= revision) return;
          const reset = frame.kind === "snapshot" || sessionId !== frame.status.sessionId;
          if (sessionId !== frame.status.sessionId) {
          }
          sessionId = frame.status.sessionId;
          revision = frame.revision;
          retries = 0;
          setStatus(frame.status);
          callbacks.current.onStatus(frame.status);
          callbacks.current.onTransport("connected");
          setComments(previous => {
            const byId = new Map((reset ? [] : previous).map(comment => [comment.commentId, comment]));
            for (const comment of frame.comments) byId.set(comment.commentId, comment);
            return Array.from(byId.values());
          });
        } catch { ws.close(); }
      };
      ws.onerror = () => ws.close();
      ws.onclose = () => {
        if (disposed || socket !== ws) return;
        callbacks.current.onTransport("disconnected");
        const delay = Math.min(30000, 500 * 2 ** Math.min(retries++, 6)) + Math.random() * 250;
        timer = setTimeout(connect, delay);
      };
    };
    connect();
    return () => { disposed = true; clearTimeout(timer); socket?.close(); };
  }, [wsUrl]);

  useEffect(() => {
    const receiveVisibleComments = (event: MessageEvent) => {
      const senderIsFacebook = event.origin === "https://facebook.com" ||
        event.origin.endsWith(".facebook.com");
      if (!senderIsFacebook || !window.opener || event.source !== window.opener ||
          event.data?.type !== "visible-facebook-comments-v1") return;
      try {
        const captured = parseCapturedComments(JSON.stringify(event.data.records));
        setCapturedById(previous => {
          const next = new Map(previous);
          for (const item of captured) next.set(item.commentId, item);
          return next;
        });
        (event.source as WindowProxy).postMessage(
          { type: "visible-facebook-comments-ack-v1", count: captured.length }, event.origin);
      } catch {
        // Ignore malformed or ambiguous rows. The Graph feed remains usable.
      }
    };
    window.addEventListener("message", receiveVisibleComments);
    return () => window.removeEventListener("message", receiveVisibleComments);
  }, []);

  useEffect(() => { props.onCount?.(comments.length); }, [comments.length, props.onCount]);
  const query = (props.search ?? "").trim().toLocaleLowerCase("vi-VN");
  const visibleComments = query ? comments.filter(comment =>
    `${comment.authorName ?? ""} ${comment.message} ${comment.commentId}`.toLocaleLowerCase("vi-VN").includes(query)) : comments;
  return <Box id="live-comments">
    <Typography variant="h2" sx={{ mb: 2 }}>Bình luận đang hiển thị ({comments.length})</Typography>
    {status?.error && <Alert severity={status.state === "ERROR" ? "error" : "warning"} sx={{ mb: 2 }}>
      {status.error}{status.retryInMillis != null ? " — đang chờ kết nối lại" : ""}
    </Alert>}
    {comments.length === 0 &&
      <Typography color="text.secondary">Chưa có bình luận. Chọn Page và nhấn Tự tìm livestream.</Typography>}
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {visibleComments.map(comment => {
        const captured = capturedById.get(comment.commentId);
        const verifiedCapture = captured?.message.trim() === comment.message.trim() ? captured : undefined;
        const authorName = comment.authorName ?? verifiedCapture?.authorName;
        return <Box component="article" key={comment.commentId} data-testid="source-comment"
        data-comment-id={comment.commentId} sx={{ p: 2, border: "1px solid " + palette.border, borderRadius: 2, bgcolor: palette.surface }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Typography fontWeight={600}>{verifiedCapture?.profileUrl ?
            <Box component="a" href={verifiedCapture.profileUrl} target="_blank" rel="noreferrer"
              sx={{ color: "inherit", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
              {authorName}
            </Box> : authorName ?? "Không có thông tin tác giả"}</Typography>
          <Box component="time" dateTime={comment.createdAt}>{new Date(comment.createdAt).toLocaleString("vi-VN")}</Box>
        </Box>
        <Typography sx={{ my: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{comment.message}</Typography>
      </Box>})}
    </Box>
  </Box>;
}
